/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

export interface ActivityLog {
  event_name: string; // a series of enums in human readable form denoting the action performed. i.e "create_integration"
  response: {
    status: number;
    description: string;
  };
  actor: {
    // object containing information specific to the initiator of the action
    email: string;
    IBMid: string; // IAMid of the user performing the action
    address: string;
  };
  event_domain: string; // Where the change was attempted
  timestamp: string; // When the activity was performed
  event: {
    request_uri: string;
    crn: string;
    method: string; // Action performed... created, updated, deleted, imported, etc.
    target: {
      id: string; // Unique identifier of the resource
      resource: string; // Type of resource
      name: string; // Name of the resource
      title?: string;
      assistant: {
        id: string;
        name: string;
        environment: {
          environment_id: string;
          name: string;
          environment: string;
        };
        skill: {
          skill_id?: string;
          name?: string;
          type?: string;
        }; // Skill may not always be applicable, such as in this example. In these events, we return an empty object.
      };
    };
  };
  _tooling_method: string;
  _tooling_description: string;
  _tooling_item: string;
}

export enum ActivityLogResources {
  ACTION = 'action',
  ASSISTANT = 'assistant',
  COLLECTION = 'collection',
  DATA_TYPE = 'saved response type',
  DIALOG = 'dialog node',
  ENTITY = 'entity',
  ENVIRONMENT = 'environment',
  EXAMPLE = 'example',
  EXTENSION = 'extension',
  INTENT = 'intent',
  CHANNEL = 'channel',
  VALUE = 'value',
  VARIABLE = 'variable',
  VERSION = 'version'
}

export enum ActivityLogDetails {
  SEARCH_ACTIVATED = 'search activated'
}

export enum ActivityLogResourceMapping {
  DIALOG = 'Dialog',
  DATA_TYPE = 'Saved Response Type',
  ASSISTANT = 'Assistant'
}

export enum ActivityLogMethods {
  CREATED = 'created',
  UPDATED = 'updated',
  DELETED = 'deleted',
  ASSIGNED = 'published',
  ADDED = 'added',
  REMOVED = 'removed'
}

export enum ActivityLogOtherMethods {
  BULK_DELETED = 'bulk deleted'
}

export interface ActivityLogFilterParams {
  user?: string;
  detail?: string;
  activity?: string[];
  environment?: string[];
  environment_ids?: string[];
  item?: string[];
  daterange?: string[];
  custom_daterange?: Date[];
}

export interface ActivityLogSortParams {
  header?: string;
  direction?: string;
}

export enum ActivityLogEventCategories {
  REFRESH = 'Refresh',
  APPLY_FILTER = 'Apply/Clear filter',
  DATA_LOADED = 'Data loaded'
}

export const AT_DESCRIPTION_MAX_LENGTH = 40;
